import React, { useCallback, useEffect, useState } from "react";
import { TrueScanResults } from "./TrueScanResults";
import { useTrueScan } from "../../../hooks/useTrueScan";
import { TrueScanScanner } from "./TrueScanScanner";
import {
  TrueScanResultsDto,
  TrueScanTrackerAcknowledgement,
} from "../../../../common/service/server/dto/TrueScanResultsDto";
import { CookieCategory } from "../../../../common/service/server/dto/CookieScanDto";
import { WebsiteAuditProgress } from "../website-audit/WebsiteAuditProgress";
import { usePolling } from "../../../../common/hooks/useInterval";
import { TrueScanAuditResults } from "./TrueScanAuditResults";
import { ExplanatoryCard, Modal, useSnackBar } from "@truevault/aquilo";
import { Callout, CalloutVariant } from "../../Callout";
import { Loading } from "../../../../common/components/Loading";
import { UUIDString } from "../../../../common/service/server/Types";

export const TrueScan = () => {
  const {
    getScan,
    runScan,
    updateTrackerConsentCategories,
    updateTrackerAcknowledgement,
    updateTrackerManagedByVendor,
    updateTrackersInBulk,
    downloadCsv,
  } = useTrueScan();
  const [trueScanData, setTrueScanData] = useState<TrueScanResultsDto | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedUrl, setSubmittedUrl] = useState<string | null>(null);
  const snackbar = useSnackBar();

  const fetchScanData = useCallback(
    async (rebake: boolean = false) => {
      try {
        const data = await getScan(rebake);
        setTrueScanData(data);
        setIsSubmitting(false);
      } catch (error) {
        console.error("Error fetching TrueScan data:", error);
      }
    },
    [getScan],
  );

  useEffect(() => {
    // On initial page load, rebake trackers (update any uncategorized with latest recommendations). Otherwise, skip the
    // lookup for performance.
    void fetchScanData(refreshTrigger === 0);
  }, [fetchScanData, refreshTrigger]);

  const handleRunScan = async (websiteUrl: string) => {
    setIsSubmitting(true);
    setSubmittedUrl(websiteUrl);
    const result = await runScan(websiteUrl);

    if (!result.success) {
      setIsSubmitting(false);
      snackbar("Failed to start scan. Please check the URL and try again.", { variant: "error" });
    } else {
      setTrueScanData((prev) =>
        prev ? { ...prev, scanInProgress: true } : { scanInProgress: true },
      );
    }

    return result;
  };

  const handleUpdateTrackerConsentCategories = async (
    trackerId: string,
    categories: CookieCategory[],
  ) => {
    const result = await updateTrackerConsentCategories(trackerId, categories);
    if (result.success) {
      // Refresh data after successful update
      setRefreshTrigger((prev) => prev + 1);
    }
    return result;
  };

  const handleUpdateTrackerAcknowledgement = async (
    trackerId: string,
    acknowledgement: TrueScanTrackerAcknowledgement,
  ) => {
    const result = await updateTrackerAcknowledgement(trackerId, acknowledgement);
    if (result.success) {
      // Refresh data after successful update
      setRefreshTrigger((prev) => prev + 1);
    }
    return result;
  };

  const handleUpdateTrackerManagedByVendor = async (
    trackerId: string,
    managedByVendor: UUIDString | null,
  ) => {
    const result = await updateTrackerManagedByVendor(trackerId, managedByVendor);
    if (result.success) {
      // Refresh data after successful update
      setRefreshTrigger((prev) => prev + 1);
    }
    return result;
  };

  const handleBulkUpdateTrackers = async (
    updates: Array<{
      trackerId: string;
      consentCategories?: CookieCategory[];
      acknowledgement?: TrueScanTrackerAcknowledgement;
      managedByVendor?: UUIDString | null;
    }>,
  ) => {
    const formattedUpdates = updates.map((update) => ({
      trackerId: update.trackerId,
      update: {
        ...(update.consentCategories !== undefined && {
          consentCategories: update.consentCategories,
        }),
        ...(update.acknowledgement !== undefined && { acknowledgement: update.acknowledgement }),
        ...(update.managedByVendor !== undefined && {
          managedByVendorUpdate: { update: true, vendorId: update.managedByVendor },
        }),
      },
    }));

    const result = await updateTrackersInBulk(formattedUpdates);
    if (result.success) {
      // Refresh data after successful update
      setRefreshTrigger((prev) => prev + 1);
    }
    return result;
  };

  const handleDownloadCsv = async () => {
    return await downloadCsv();
  };

  const [showOnboardingModal, setShowOnboardingModal] = useState<boolean>(false);
  const onboardingModalMediaStyle = {
    width: "635px",
    height: "auto",
    objectFit: "contain" as const,
    display: "inline",
  };

  // Use a useEffect to set the onboarding modal visibility because the scan data is fetched asynchronously; avoid flash
  // of the modal if the scan data is already available.
  useEffect(() => {
    if (!trueScanData) {
      setShowOnboardingModal(false);
      return;
    }

    const hasRunScan = trueScanData?.lastScan || trueScanData?.scanInProgress;
    if (!hasRunScan) {
      setShowOnboardingModal(true);
    }
  }, [trueScanData, trueScanData?.lastScan, trueScanData?.scanInProgress]);

  usePolling(() => setRefreshTrigger((prev) => prev + 1), 4000, trueScanData?.scanInProgress);

  if (!trueScanData) {
    return <Loading />;
  }

  if (trueScanData?.scanInProgress) {
    return (
      <div className="mt-md">
        <WebsiteAuditProgress url={submittedUrl} />
      </div>
    );
  }

  return (
    <>
      {trueScanData?.hasRecentFailedScan && (
        <Callout variant={CalloutVariant.Yellow} className="mt-md">
          <p>Your most recent scan failed. Please check your website URL and try again.</p>
        </Callout>
      )}
      {trueScanData?.lastScan && !trueScanData?.hasRecentFailedScan && (
        <div className="tw-boundary">
          <div className="max-w-[944px] mt-md">
            <ExplanatoryCard
              topBody={
                <div>
                  <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                    What to do after running a TrueScan?
                  </h3>
                  <p className="text-sm font-normal leading-5 text-gray-500">
                    TrueScan identifies all network requests your website sends to third parties via
                    scripts. Using your categorization of these scripts in the table below, TrueScan
                    will check your website to see if it is truly blocking requests when a website
                    visitor declines tracking in your consent banner. If you see "No" in the
                    "Respecting Consents" column for a script listed below, it means your tag
                    delivery setup does not follow how you've categorized that script.
                  </p>
                </div>
              }
              hiddenBody={
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3 pt-3">
                      Next Steps for Implementation
                    </h3>
                    <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                      <p>
                        <strong>1. Review and Categorize:</strong> Assign consent categories
                        (Analytics, Advertising, Personalization, etc.) to each detected script
                        based on its purpose and data collection practices.
                      </p>
                      <p>
                        <strong>2. Configure Google Tag Manager:</strong> Set up consent
                        requirements in GTM for the tags corresponding to each script using the
                        Consent Settings in Tag Configuration. This requires the{" "}
                        <a
                          href="https://developers.truevault.com/docs/google-tag-manager/setup-truevault-cmp-template"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 underline"
                        >
                          TrueVault CMP Template setup
                        </a>{" "}
                        to be completed in GTM.
                      </p>
                      <p>
                        <strong>3. Identify Scripts Managed by a Platform (e.g., Shopify):</strong>{" "}
                        Scripts loaded by a platform like Shopify will always load and the platform
                        will communicate consent signals directly to the App Pixel. For those
                        scripts, mark them as platform-managed in the table to prevent TrueScan from
                        flagging them as not respecting consent. See our guide on{" "}
                        <a
                          href="https://developers.truevault.com/docs/shopify/shopify-app-pixel-consent"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 underline"
                        >
                          Shopify App Pixels
                        </a>{" "}
                        for more information.
                      </p>
                      <p>
                        <strong>4. Test and Monitor:</strong> Run TrueScan regularly to detect new
                        scripts and ensure your consent implementation continues to work correctly.
                      </p>
                      <p>
                        <strong>Need detailed guidance?</strong>{" "}
                        <a
                          href="https://knowledge.truevault.com/article/352-what-to-do-after-running-truescan"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 underline"
                        >
                          View our complete TrueScan Implementation Guide
                        </a>{" "}
                        for step-by-step instructions on implementing consent requirements based on
                        your scan results.
                      </p>
                    </div>
                  </div>
                </div>
              }
            />
          </div>
        </div>
      )}
      <TrueScanScanner
        onStartScan={handleRunScan}
        suggestedUrl={trueScanData?.suggestedWebsiteUrl}
        inProgress={trueScanData?.scanInProgress || isSubmitting}
      />
      {trueScanData?.lastScan && !trueScanData?.hasRecentFailedScan && (
        <TrueScanResults
          {...trueScanData}
          className="mt-md"
          onUpdateTrackerConsentCategories={handleUpdateTrackerConsentCategories}
          onUpdateTrackerAcknowledgement={handleUpdateTrackerAcknowledgement}
          onUpdateTrackerManagedByVendor={handleUpdateTrackerManagedByVendor}
          onBulkUpdateTrackers={handleBulkUpdateTrackers}
          onDownloadCsv={handleDownloadCsv}
        />
      )}
      {trueScanData?.lastScan && !trueScanData?.hasRecentFailedScan && (
        <TrueScanAuditResults cmpAudit={trueScanData?.cmpAudit} />
      )}
      {trueScanData?.lastScan && !trueScanData?.hasRecentFailedScan && (
        <TrueScanScanner
          onStartScan={handleRunScan}
          suggestedUrl={trueScanData?.suggestedWebsiteUrl}
          inProgress={trueScanData?.scanInProgress || isSubmitting}
        />
      )}
      <details style={{ display: "none" }}>
        <summary>Debug Info</summary>
        <p>TrueScan data:</p>
        <div style={{ backgroundColor: "#ddd", padding: "10px", borderRadius: "5px" }}>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {JSON.stringify(trueScanData, null, 2)}
          </pre>
        </div>
      </details>
      {showOnboardingModal && (
        <Modal
          title="Make ongoing website compliance easy"
          body="Use TrueScan to routinely scan your website for 3rd party scripts and validate your consent management setup."
          firstButton="Continue"
          firstButtonColor="primary"
          widthButton="full"
          showOverlay={true}
          closeOnOverlayClick={true}
          onFirstButtonClick={() => setShowOnboardingModal(false)}
        >
          <img
            src="/assets/images/stay_compliant/privacy_center/truescan-onboarding-modal.svg"
            alt=""
            className="mx-auto mt-md"
            style={onboardingModalMediaStyle}
          />
        </Modal>
      )}
    </>
  );
};
